import type { ServerMessage, PublicMember } from '@shared/protocol';
import './styles.css';

interface Question {
  id: number;
  text: string;
  choices: [string, string, string, string];
  correctIdx: number;
  active: boolean;
  createdAt: number;
}

const TOKEN_KEY = 'naibday-admin-token';
const ROOMS_KEY = 'naibday-admin-rooms';
const root = document.getElementById('admin-app') as HTMLElement;

// Sprite constants live here (not next to the upload helpers below) because the
// dashboard renders BEFORE function bodies execute, and a `const` doesn't hoist
// like a `function` declaration — referencing it earlier would TDZ-throw.
type SpriteKind = 'character' | 'opponent' | 'hazard' | 'prize' | 'finish';
const SPRITE_KINDS: SpriteKind[] = ['character', 'opponent', 'hazard', 'prize', 'finish'];
const SPRITE_SIZE = 256;

function loadStoredRoomIds(): string[] {
  try {
    const raw = localStorage.getItem(ROOMS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

function persistRoomIds(ids: string[]): void {
  localStorage.setItem(ROOMS_KEY, JSON.stringify(ids));
}

let token: string | null = localStorage.getItem(TOKEN_KEY);

// Module state hoisted above the entry point: any `const` referenced from
// `renderDashboard`/`startWatchingRoom`/`loadQuestions` (which are called
// immediately on this line below) must be initialized first or it TDZ-throws.
let questions: Question[] = [];

interface WatchedRoom {
  ws: WebSocket;
  members: Map<string, PublicMember>;
  status: 'lobby' | 'racing' | 'ended';
  raceStartedAt?: number;
  /** True while we've lost the WS and a reconnect attempt is in flight. */
  reconnecting?: boolean;
  /** Timer id for pending reconnect attempt (so we can cancel on forget). */
  reconnectTimer?: number;
}
const watchedRooms = new Map<string, WatchedRoom>();

if (token) renderDashboard();
else renderLogin();

// ---------- Login ----------

function renderLogin(): void {
  root.innerHTML = `
    <main class="form-card admin-login">
      <h1>Naibday — Admin</h1>
      <form id="login-form" autocomplete="off">
        <label>
          Password
          <input name="password" type="password" required autofocus />
        </label>
        <button type="submit">Sign in</button>
        <p class="form-error" id="login-error" hidden></p>
      </form>
    </main>
  `;
  const form = document.getElementById('login-form') as HTMLFormElement;
  const errEl = document.getElementById('login-error') as HTMLParagraphElement;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errEl.hidden = true;
    const password = (form.elements.namedItem('password') as HTMLInputElement).value;
    const resp = await fetch('/api/admin/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (!resp.ok) {
      errEl.textContent = 'Invalid password.';
      errEl.hidden = false;
      return;
    }
    const data = (await resp.json()) as { token: string };
    token = data.token;
    localStorage.setItem(TOKEN_KEY, token);
    renderDashboard();
  });
}

// ---------- Dashboard ----------

function authHeaders(): HeadersInit {
  return token ? { authorization: `Bearer ${token}` } : {};
}

function renderDashboard(): void {
  root.innerHTML = `
    <header class="admin-header">
      <h1>Naibday Admin</h1>
      <button id="logout" class="btn-ghost">Sign out</button>
    </header>

    <div id="q-banner"></div>

    <section class="admin-section">
      <div class="admin-section__head">
        <h2>Rooms</h2>
        <button id="create-room" class="btn-primary">+ Create room</button>
      </div>
      <div id="rooms-list" class="rooms-list">
        <p class="muted">No rooms yet. Create one to share with players.</p>
      </div>
    </section>

    <section class="admin-section">
      <div class="admin-section__head">
        <h2>Sprites</h2>
      </div>
      <p class="muted">Upload custom artwork for the three game elements. Each is center-cropped and resized to 256×256 PNG. Transparent backgrounds work great.</p>
      ${spriteCardHtml('character', 'Character', 'The runner — your own character.')}
      ${spriteCardHtml('opponent', 'Opponent', 'How other players appear in your view (all rivals share this sprite).')}
      ${spriteCardHtml('hazard', 'Hazard', 'Ground obstacle players jump over. Hitting it costs −2 points.')}
      ${spriteCardHtml('prize', 'Prize', 'Wiggling in the sky. Touching it triggers a trivia question.')}
      ${spriteCardHtml('finish', 'Finish line', 'End of the course. A flag, banner, or whatever you like.')}
    </section>

    <section class="admin-section">
      <div class="admin-section__head">
        <h2>Trivia questions</h2>
        <button id="add-q" class="btn-primary">+ Add question</button>
      </div>
      <div id="q-editor"></div>
      <div id="q-list" class="q-list"><p class="muted">Loading…</p></div>
    </section>

    <section class="admin-section">
      <div class="admin-section__head">
        <h2>Bulk import questions</h2>
      </div>
      <p class="muted">Paste a CSV (one row per question: <code>text,A,B,C,D,correctIdx</code>) or a JSON array. correctIdx is 0–3.</p>
      <textarea id="bulk-input" rows="6" placeholder="What is 2+2?,3,4,5,22,1&#10;Capital of France?,London,Paris,Madrid,Rome,1"></textarea>
      <div class="bulk-actions">
        <button id="bulk-import" class="btn-primary">Import</button>
        <span id="bulk-status" class="muted"></span>
      </div>
    </section>
  `;
  document.getElementById('logout')!.addEventListener('click', logout);
  document.getElementById('create-room')!.addEventListener('click', () => void createRoom());
  document.getElementById('add-q')!.addEventListener('click', () => openEditor(null));
  document.getElementById('bulk-import')!.addEventListener('click', () => void importBulk());
  for (const kind of SPRITE_KINDS) {
    wireSpriteUpload(kind);
    void refreshSpritePreview(kind);
  }
  void loadQuestions();

  // Re-watch any rooms we knew about from a previous session (D-4.2)
  for (const id of loadStoredRoomIds()) startWatchingRoom(id);
}

function renderQuestionPoolBanner(): void {
  const banner = document.getElementById('q-banner');
  if (!banner) return;
  const activeCount = questions.filter((q) => q.active).length;
  if (activeCount === 0) {
    banner.innerHTML = `
      <div class="banner banner--warn">
        <strong>No active questions.</strong> Add at least one before starting a race —
        the server will reject Start otherwise.
      </div>`;
  } else if (activeCount < 10) {
    banner.innerHTML = `
      <div class="banner banner--info">
        <strong>${activeCount} active question${activeCount === 1 ? '' : 's'}.</strong>
        Each race asks 10 — players will see repeats. Add more for variety.
      </div>`;
  } else {
    banner.innerHTML = '';
  }
}

function logout(): void {
  for (const watch of watchedRooms.values()) watch.ws.close();
  watchedRooms.clear();
  localStorage.removeItem(TOKEN_KEY);
  token = null;
  renderLogin();
}

// ---------- Rooms ----------

async function createRoom(): Promise<void> {
  const resp = await fetch('/api/admin/rooms', { method: 'POST', headers: authHeaders() });
  if (resp.status === 401) return logout();
  if (!resp.ok) {
    alert('Failed to create room.');
    return;
  }
  const data = (await resp.json()) as { roomId: string };
  startWatchingRoom(data.roomId);
  // Persist the new room id (D-4.2)
  const ids = loadStoredRoomIds();
  if (!ids.includes(data.roomId)) {
    ids.push(data.roomId);
    persistRoomIds(ids);
  }
}

function forgetRoom(roomId: string): void {
  // Permanently remove from localStorage FIRST so the close handler's reconnect
  // check (which queries localStorage) bails on the in-flight socket close.
  persistRoomIds(loadStoredRoomIds().filter((id) => id !== roomId));
  const watch = watchedRooms.get(roomId);
  if (watch) {
    if (watch.reconnectTimer) clearTimeout(watch.reconnectTimer);
    watch.ws.close();
    watchedRooms.delete(roomId);
  }
  renderRooms();
}

function startWatchingRoom(roomId: string): void {
  // If we already have a watcher (with a live WS or one in mid-reconnect),
  // don't double-attach. forgetRoom() is the only way to drop a room.
  if (watchedRooms.has(roomId)) return;
  attachAdminWs(roomId, false);
}

/**
 * Open (or re-open) the admin's WS for a single room.
 *
 * Pre-2026-05-10 the close handler `delete()`d from `watchedRooms`, so any
 * dropped connection (iOS backgrounded the tab, screen lock, network blip)
 * made the room card vanish from the admin panel — even though localStorage
 * still had the id. Now: we keep the WatchedRoom alive across drops, render
 * a "reconnecting" pill, and re-open the WS with backoff. Only `forgetRoom`
 * actually removes a card. See post-v1 changelog §33.
 */
function attachAdminWs(roomId: string, isReconnect: boolean): void {
  const wsUrl = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
  const ws = new WebSocket(wsUrl);
  const existing = watchedRooms.get(roomId);
  const members = existing?.members ?? new Map<string, PublicMember>();
  const watch: WatchedRoom = {
    ws,
    members,
    status: existing?.status ?? 'lobby',
    raceStartedAt: existing?.raceStartedAt,
    reconnecting: isReconnect,
  };
  if (existing?.reconnectTimer) {
    clearTimeout(existing.reconnectTimer);
  }
  watchedRooms.set(roomId, watch);
  if (isReconnect) renderRooms();

  ws.addEventListener('open', () => {
    watch.reconnecting = false;
    ws.send(
      JSON.stringify({ type: 'join', roomId, name: 'admin', role: 'admin', token }),
    );
  });

  ws.addEventListener('message', (e) => {
    const msg = JSON.parse(e.data) as ServerMessage;
    switch (msg.type) {
      case 'joined':
        members.clear();
        for (const m of msg.members) members.set(m.playerId, m);
        renderRooms();
        break;
      case 'player_joined':
        members.set(msg.member.playerId, msg.member);
        renderRooms();
        break;
      case 'player_left':
        members.delete(msg.playerId);
        renderRooms();
        break;
      case 'race_start':
        watch.status = 'racing';
        watch.raceStartedAt = msg.startAt;
        renderRooms();
        break;
      case 'race_end':
        watch.status = 'ended';
        renderRooms();
        break;
      case 'room_reset':
        watch.status = 'lobby';
        watch.raceStartedAt = undefined;
        renderRooms();
        break;
      case 'error':
        console.error('[admin ws]', msg);
        if (msg.code === 'unknown_room') {
          // Stored room no longer exists — clean it up silently
          forgetRoom(roomId);
        } else if (msg.code === 'no_questions') {
          alert('Add at least one trivia question before starting a race.');
        } else if (msg.code === 'room_not_lobby') {
          alert('Race already started or ended.');
        } else if (msg.code === 'room_not_ended') {
          alert('Room is not in ended state — cannot reset.');
        }
        break;
    }
  });

  ws.addEventListener('close', () => {
    const watch = watchedRooms.get(roomId);
    if (!watch) return;
    // If the user explicitly forgot the room while the WS was in flight, don't
    // try to reconnect.
    if (!loadStoredRoomIds().includes(roomId)) return;
    watch.reconnecting = true;
    renderRooms();
    // Immediate retry feels right here — most disconnects are an iOS
    // background or a brief network blip; a 2 s delay just makes the room
    // look broken longer than it actually is.
    watch.reconnectTimer = window.setTimeout(() => {
      watchedRooms.delete(roomId);
      attachAdminWs(roomId, true);
    }, 2000);
  });
}

function startRace(roomId: string): void {
  const watch = watchedRooms.get(roomId);
  if (!watch) return;
  watch.ws.send(JSON.stringify({ type: 'admin_start' }));
}

function resetRoom(roomId: string): void {
  const watch = watchedRooms.get(roomId);
  if (!watch) return;
  watch.ws.send(JSON.stringify({ type: 'admin_reset' }));
}

function renderRooms(): void {
  const container = document.getElementById('rooms-list');
  if (!container) return;
  if (watchedRooms.size === 0) {
    container.innerHTML = `<p class="muted">No rooms yet. Create one to share with players.</p>`;
    return;
  }
  container.innerHTML = '';
  for (const [roomId, watch] of watchedRooms) {
    const players = [...watch.members.values()].filter((m) => m.role === 'player');
    const card = document.createElement('div');
    card.className = `room-card room-card--${watch.status}`;
    const action =
      watch.status === 'lobby'
        ? `<button data-start="${roomId}" class="btn-primary"${players.length === 0 ? ' disabled' : ''}>Start race</button>`
        : watch.status === 'racing'
          ? `<span class="room-card__status">⏱ Racing</span>`
          : `<button data-replay="${roomId}" class="btn-primary">Replay</button>`;
    const reconnectBadge = watch.reconnecting
      ? ` <span class="room-card__reconnecting">reconnecting…</span>`
      : '';
    card.innerHTML = `
      <div class="room-card__head">
        <span class="room-card__id">${roomId}</span>
        <span class="muted">${players.length} player${players.length === 1 ? '' : 's'} connected${reconnectBadge}</span>
      </div>
      <ul class="players">
        ${
          players.length === 0
            ? `<li class="muted">no players yet — share <strong>${roomId}</strong> with friends</li>`
            : players
                .map(
                  (p) =>
                    `<li><span class="badge player">player</span> ${escapeHtml(p.name)}</li>`,
                )
                .join('')
        }
      </ul>
      <div class="room-card__actions">
        ${action}
        <button data-spectate="${roomId}" class="btn-ghost">Big-screen view</button>
        <button data-forget="${roomId}" class="btn-ghost">Forget</button>
      </div>
    `;
    container.appendChild(card);
  }
  container.querySelectorAll<HTMLButtonElement>('[data-start]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const id = btn.dataset.start;
      if (id) startRace(id);
    }),
  );
  container.querySelectorAll<HTMLButtonElement>('[data-replay]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const id = btn.dataset.replay;
      if (id) resetRoom(id);
    }),
  );
  container.querySelectorAll<HTMLButtonElement>('[data-forget]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const id = btn.dataset.forget;
      if (id) forgetRoom(id);
    }),
  );
  container.querySelectorAll<HTMLButtonElement>('[data-spectate]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const id = btn.dataset.spectate;
      if (id) {
        // Open in a new tab so the admin panel stays usable on the operator's
        // laptop while the big screen displays the spectator view.
        window.open(`/spectate.html?room=${encodeURIComponent(id)}`, '_blank');
      }
    }),
  );
}

// ---------- Questions ----------

async function loadQuestions(): Promise<void> {
  const resp = await fetch('/api/admin/questions', { headers: authHeaders() });
  if (resp.status === 401) return logout();
  questions = (await resp.json()) as Question[];
  renderQuestionList();
}

function renderQuestionList(): void {
  const container = document.getElementById('q-list');
  if (!container) return;
  renderQuestionPoolBanner();
  if (questions.length === 0) {
    container.innerHTML = `<p class="muted">No questions yet. Click "Add question" to start.</p>`;
    return;
  }
  container.innerHTML = '';
  for (const q of questions) {
    const item = document.createElement('div');
    item.className = 'q-item';
    item.innerHTML = `
      <div class="q-item__head">
        <span class="q-item__num">#${q.id}</span>
        <span class="q-item__text">${escapeHtml(q.text)}</span>
      </div>
      <ul class="q-item__choices">
        ${q.choices
          .map(
            (c, i) =>
              `<li class="${i === q.correctIdx ? 'correct' : ''}">${String.fromCharCode(65 + i)}. ${escapeHtml(c)}</li>`,
          )
          .join('')}
      </ul>
      <div class="q-item__actions">
        <button data-edit="${q.id}" class="btn-ghost">Edit</button>
        <button data-delete="${q.id}" class="btn-danger">Delete</button>
      </div>
    `;
    container.appendChild(item);
  }
  container.querySelectorAll<HTMLButtonElement>('[data-edit]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const id = Number(btn.dataset.edit);
      openEditor(questions.find((q) => q.id === id) ?? null);
    }),
  );
  container.querySelectorAll<HTMLButtonElement>('[data-delete]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this question?')) return;
      const id = Number(btn.dataset.delete);
      const resp = await fetch(`/api/admin/questions/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (resp.status === 401) return logout();
      await loadQuestions();
    }),
  );
}

function openEditor(q: Question | null): void {
  const editor = document.getElementById('q-editor');
  if (!editor) return;
  editor.innerHTML = `
    <form id="q-form" class="q-form">
      <h3>${q ? `Edit question #${q.id}` : 'New question'}</h3>
      <label>Question text
        <input name="text" required maxlength="500" value="${escapeAttr(q?.text ?? '')}" />
      </label>
      ${[0, 1, 2, 3]
        .map(
          (i) => `
        <label>Choice ${String.fromCharCode(65 + i)}
          <input name="c${i}" required maxlength="200" value="${escapeAttr(q?.choices[i] ?? '')}" />
        </label>`,
        )
        .join('')}
      <label>Correct answer
        <select name="correctIdx">
          ${[0, 1, 2, 3]
            .map(
              (i) =>
                `<option value="${i}" ${q?.correctIdx === i ? 'selected' : ''}>${String.fromCharCode(65 + i)}</option>`,
            )
            .join('')}
        </select>
      </label>
      <div class="q-form__actions">
        <button type="submit" class="btn-primary">${q ? 'Save' : 'Create'}</button>
        <button type="button" id="q-cancel" class="btn-ghost">Cancel</button>
      </div>
    </form>
  `;
  document.getElementById('q-cancel')!.addEventListener('click', () => {
    editor.innerHTML = '';
  });
  document.getElementById('q-form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    const body = {
      text: String(fd.get('text') ?? '').trim(),
      choices: [
        String(fd.get('c0') ?? '').trim(),
        String(fd.get('c1') ?? '').trim(),
        String(fd.get('c2') ?? '').trim(),
        String(fd.get('c3') ?? '').trim(),
      ] as [string, string, string, string],
      correctIdx: Number(fd.get('correctIdx')),
    };
    const url = q ? `/api/admin/questions/${q.id}` : '/api/admin/questions';
    const method = q ? 'PUT' : 'POST';
    const resp = await fetch(url, {
      method,
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    if (resp.status === 401) return logout();
    if (!resp.ok) {
      alert('Could not save question.');
      return;
    }
    editor.innerHTML = '';
    await loadQuestions();
  });
}

function spriteCardHtml(kind: SpriteKind, title: string, hint: string): string {
  return `
    <div class="sprite-card">
      <div class="sprite-card__head">
        <h3>${title}</h3>
        <span class="muted">${hint}</span>
      </div>
      <div class="sprite-row">
        <img id="sprite-preview-${kind}" class="sprite-preview" alt="" />
        <input type="file" id="sprite-file-${kind}" accept="image/*" />
        <span id="sprite-status-${kind}" class="muted"></span>
      </div>
    </div>
  `;
}

function wireSpriteUpload(kind: SpriteKind): void {
  const fileInput = document.getElementById(`sprite-file-${kind}`) as HTMLInputElement;
  const status = document.getElementById(`sprite-status-${kind}`) as HTMLSpanElement;
  const preview = document.getElementById(`sprite-preview-${kind}`) as HTMLImageElement;
  if (!fileInput || !status || !preview) return;

  // Auto-upload on file pick. The two-step flow (pick → click Upload) used to
  // leave users thinking the file was saved when only the local preview had
  // rendered. This way "I saw it" = "the server has it".
  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    status.style.color = '';

    // For 'finish' we preserve the original file (GIF animation, etc.). For
    // the other kinds we resize to 256×256 PNG to keep canvas-rendered
    // obstacles consistent and small.
    let blob: Blob;
    let contentType: string;
    if (kind === 'finish') {
      if (file.size > 2 * 1024 * 1024) {
        status.textContent = 'File larger than 2 MB';
        status.style.color = '#ff8a80';
        return;
      }
      blob = file;
      contentType = file.type || 'image/png';
      preview.src = URL.createObjectURL(blob);
      status.textContent = 'Uploading original…';
    } else {
      status.textContent = 'Resizing…';
      try {
        blob = await resizeImageToBlob(file, SPRITE_SIZE);
        contentType = 'image/png';
        preview.src = URL.createObjectURL(blob);
      } catch (e) {
        status.textContent = `Resize error: ${(e as Error).message}`;
        status.style.color = '#ff8a80';
        return;
      }
      status.textContent = `Uploading ${(blob.size / 1024).toFixed(0)} KB…`;
    }
    fileInput.disabled = true;
    try {
      const resp = await fetch(`/api/admin/sprites/${kind}`, {
        method: 'POST',
        headers: { 'content-type': contentType, ...authHeaders() },
        body: blob,
      });
      if (resp.status === 401) {
        logout();
        return;
      }
      if (!resp.ok) {
        status.textContent = `Upload failed (HTTP ${resp.status})`;
        status.style.color = '#ff8a80';
        return;
      }
      const data = (await resp.json()) as { ok: boolean; bytes: number };
      status.textContent = `✓ Uploaded (${(data.bytes / 1024).toFixed(0)} KB)`;
      status.style.color = '#80e27e';
      await refreshSpritePreview(kind);
    } catch (e) {
      status.textContent = `Upload error: ${(e as Error).message}`;
      status.style.color = '#ff8a80';
    } finally {
      fileInput.disabled = false;
      fileInput.value = '';
    }
  });
}

async function refreshSpritePreview(kind: SpriteKind): Promise<void> {
  const preview = document.getElementById(`sprite-preview-${kind}`) as HTMLImageElement | null;
  if (!preview) return;
  const r = await fetch(`/api/sprites/${kind}/exists`);
  const data = (await r.json()) as { exists: boolean };
  if (data.exists) {
    preview.src = `/api/sprites/${kind}?t=${Date.now()}`;
  } else {
    preview.removeAttribute('src');
  }
}

/** Center-crop + downscale an image File to a square PNG Blob (D-5.3). */
async function resizeImageToBlob(file: File, size: number): Promise<Blob> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error ?? new Error('read failed'));
    r.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('image decode failed'));
    i.src = dataUrl;
  });
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context not available');
  // Center-crop the larger dimension
  const srcSize = Math.min(img.width, img.height);
  const sx = (img.width - srcSize) / 2;
  const sy = (img.height - srcSize) / 2;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, srcSize, srcSize, 0, 0, size, size);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/png'),
  );
  if (!blob) throw new Error('encode failed');
  return blob;
}

// ---------- Bulk import ----------

async function importBulk(): Promise<void> {
  const textarea = document.getElementById('bulk-input') as HTMLTextAreaElement;
  const status = document.getElementById('bulk-status') as HTMLSpanElement;
  const raw = textarea.value.trim();
  if (!raw) {
    status.textContent = 'Paste some questions first.';
    return;
  }

  let payload: Array<{
    text: string;
    choices: [string, string, string, string];
    correctIdx: number;
  }>;
  try {
    payload = raw.startsWith('[') ? parseBulkJson(raw) : parseBulkCsv(raw);
  } catch (e) {
    status.textContent = `Parse error: ${(e as Error).message}`;
    return;
  }
  if (payload.length === 0) {
    status.textContent = 'No rows parsed.';
    return;
  }

  status.textContent = `Sending ${payload.length}…`;
  const resp = await fetch('/api/admin/questions/bulk', {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ questions: payload }),
  });
  if (resp.status === 401) {
    logout();
    return;
  }
  if (!resp.ok) {
    const data = (await resp.json().catch(() => ({}))) as { details?: unknown };
    status.textContent = `Import failed${data.details ? ' — see console' : ''}`;
    if (data.details) console.warn('bulk import details', data.details);
    return;
  }
  const data = (await resp.json()) as { inserted: number };
  status.textContent = `Imported ${data.inserted} questions.`;
  textarea.value = '';
  await loadQuestions();
}

function parseBulkJson(raw: string): Array<{
  text: string;
  choices: [string, string, string, string];
  correctIdx: number;
}> {
  const arr = JSON.parse(raw);
  if (!Array.isArray(arr)) throw new Error('expected JSON array');
  return arr.map((row: unknown, i: number) => {
    const r = row as Record<string, unknown>;
    if (typeof r.text !== 'string') throw new Error(`row ${i + 1}: missing text`);
    if (!Array.isArray(r.choices) || r.choices.length !== 4) {
      throw new Error(`row ${i + 1}: choices must be a length-4 array`);
    }
    const correctIdx = Number(r.correctIdx);
    if (!Number.isInteger(correctIdx) || correctIdx < 0 || correctIdx > 3) {
      throw new Error(`row ${i + 1}: correctIdx must be 0..3`);
    }
    return {
      text: String(r.text),
      choices: r.choices.map(String) as [string, string, string, string],
      correctIdx,
    };
  });
}

function parseBulkCsv(raw: string): Array<{
  text: string;
  choices: [string, string, string, string];
  correctIdx: number;
}> {
  const rows: string[][] = [];
  for (const line of raw.split(/\r?\n/)) {
    if (!line.trim()) continue;
    rows.push(parseCsvLine(line));
  }
  return rows.map((cols, i) => {
    if (cols.length !== 6) {
      throw new Error(`row ${i + 1}: expected 6 columns (text,A,B,C,D,correctIdx) — got ${cols.length}`);
    }
    const [text, a, b, c, d, idxRaw] = cols;
    const correctIdx = Number(idxRaw);
    if (!Number.isInteger(correctIdx) || correctIdx < 0 || correctIdx > 3) {
      throw new Error(`row ${i + 1}: correctIdx must be 0..3`);
    }
    return {
      text: text!.trim(),
      choices: [a!.trim(), b!.trim(), c!.trim(), d!.trim()],
      correctIdx,
    };
  });
}

/** Parse a single CSV line: comma-separated, supports "quoted, fields" with "" escaping. */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += c;
      }
    } else {
      if (c === ',') {
        out.push(cur);
        cur = '';
      } else if (c === '"') {
        inQuotes = true;
      } else {
        cur += c ?? '';
      }
    }
  }
  out.push(cur);
  return out;
}

// ---------- Utils ----------

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );
}
function escapeAttr(s: string): string {
  return escapeHtml(s);
}
